import * as React from "react";
import PropTypes from "prop-types";
import axios from "axios";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from "@mui/material";

import { API_BASE } from "../lib/api";
import { authFieldSx } from "../pages/authFieldStyles";

const primaryButtonSx = {
  "&&": {
    backgroundColor: "#f7b900",
    backgroundImage: "none",
    color: "#171717",
    borderColor: "#f7b900",
    boxShadow: "none",
  },
  "&&:hover, &&:focus-visible": {
    backgroundColor: "#d99b00",
    backgroundImage: "none",
    color: "#171717",
    borderColor: "#d99b00",
    boxShadow: "none",
  },
  fontWeight: 700,
};

function ForgotPassword({ open, handleClose, defaultEmail = "" }) {
  const [step, setStep] = React.useState(1);
  const [email, setEmail] = React.useState(defaultEmail);
  const [code, setCode] = React.useState("");
  const [newPassword, setNewPassword] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [msg, setMsg] = React.useState("");
  const [err, setErr] = React.useState("");

  React.useEffect(() => {
    if (open) {
      setStep(1);
      setEmail(defaultEmail || "");
      setCode("");
      setNewPassword("");
      setMsg("");
      setErr("");
    }
  }, [open, defaultEmail]);

  const onRequestCode = async () => {
    setErr("");
    setMsg("");
    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      setErr("Please enter a valid email.");
      return;
    }

    try {
      setLoading(true);
      const res = await axios.post(`${API_BASE}/password/forgot`, { email });
      const debugCode = res.data?.debug_code;
      if (debugCode) {
        setCode(debugCode);
        setMsg(`Email could not be sent. DEBUG reset code: ${debugCode}`);
      } else {
        setMsg("If an account exists for this email, we have sent a 6-digit code.");
      }
      setStep(2);
    } catch (e) {
      setErr(e?.response?.data?.error || "Could not start reset.");
    } finally {
      setLoading(false);
    }
  };

  const onConfirmReset = async () => {
    setErr("");
    setMsg("");
    if (!code || code.length !== 6) {
      setErr("Please enter the 6-digit code.");
      return;
    }
    if (!newPassword || newPassword.length < 6) {
      setErr("New password must be at least 6 characters.");
      return;
    }

    try {
      setLoading(true);
      await axios.post(`${API_BASE}/password/reset_confirm`, {
        email,
        code,
        new_password: newPassword,
      });
      setMsg("Password has been reset. You can now sign in.");
      setTimeout(() => handleClose(), 800);
    } catch (e) {
      setErr(e?.response?.data?.error || "Reset failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
      <DialogTitle>Reset password</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {step === 1 ? (
            <>
              <TextField
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                fullWidth
                autoFocus
                sx={authFieldSx}
              />
              <Alert severity="info">
                Enter your email and we will send you a 6-digit code to reset your password.
              </Alert>
            </>
          ) : (
            <>
              <TextField
                label="6-digit code"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                fullWidth
                inputProps={{ inputMode: "numeric", pattern: "[0-9]*", maxLength: 6 }}
                sx={authFieldSx}
              />
              <TextField
                label="New password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                fullWidth
                helperText="Min 6 characters"
                sx={authFieldSx}
              />
              <Alert severity="info">
                Did not get the code? Try again in a minute from the previous screen.
              </Alert>
            </>
          )}
          {err ? <Alert severity="error">{err}</Alert> : null}
          {msg ? <Alert severity="success">{msg}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        {step === 1 ? (
          <Button
            onClick={onRequestCode}
            variant="contained"
            disabled={loading}
            sx={primaryButtonSx}
          >
            {loading ? "Sending..." : "Send code"}
          </Button>
        ) : (
          <Button
            onClick={onConfirmReset}
            variant="contained"
            disabled={loading}
            sx={primaryButtonSx}
          >
            {loading ? "Resetting..." : "Reset password"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

ForgotPassword.propTypes = {
  handleClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  defaultEmail: PropTypes.string,
};

export default ForgotPassword;
